import { AppColors } from "@/styles/colors";
import {
  Checkbox,
  CheckboxCheckedChangeDetails,
  Flex,
  Table,
  Text,
} from "@chakra-ui/react";
import {
  Attributes,
  cloneElement,
  Dispatch,
  ReactElement,
  SetStateAction,
} from "react";
import "@/components/common/section-components/card/text-elements.css";

interface Props {
  indeterminate?: boolean;
  setSelection: Dispatch<SetStateAction<string[]>>;
  selection: string[];
  headers: { label: string; value: string; align?: string }[];
  items: any[];
  noActions?: boolean;
  noSelection?: boolean;
  actionElements?: (item: any) => ReactElement;
  multipleSelection?: boolean;
  actionsHeaderLabel?: string;
  headerLabelAlign?: string;
  actionsAlign?: string;
  rowId?: string;
  noBorder?: boolean;
}

export const StandardTable = ({
  indeterminate,
  selection,
  headers,
  items,
  noActions,
  noSelection,
  actionsHeaderLabel = "Acciones",
  actionsAlign,
  headerLabelAlign,
  multipleSelection = true,
  rowId = "id",
  noBorder,
  actionElements,
  setSelection,
}: Props) => {
  const handleHeaderCheckedChange = (changes: CheckboxCheckedChangeDetails) => {
    if (!multipleSelection) return;
    setSelection(changes?.checked ? items?.map((item) => item?.[rowId]) : []);
  };

  const handleItemCheckChange = (
    changes: CheckboxCheckedChangeDetails,
    item: any,
  ) => {
    setSelection((prev) => {
      if (!multipleSelection) {
        return changes.checked ? [item?.[rowId]] : [];
      }

      const updated = new Set(prev);
      if (changes.checked) {
        updated.add(item?.[rowId]);
      } else {
        updated.delete(item?.[rowId]);
      }
      return Array.from(updated);
    });
  };

  return (
    <Table.ScrollArea
      data-testid="standard-table-component"
      borderRadius="8px"
      css={{
        /* Estas son medidas generales */
        /* Para mejorar, podríamos tomar donde queremos que termine la tabla
          y aplicar la medida correspondiente dependiendo el navegador
        */
        "@media (max-width: 1350px) and (min-width: 1272px)": {
          maxWidth: "900px",
          height: "320px",
        },
        "@media (max-width: 1423px) and (min-width: 1351px)": {
          maxWidth: "1025px",
          height: "390px",
        },
        "@media (max-width: 1600px) and (min-width: 1424px)": {
          maxWidth: "1230px",
          height: "450px",
        },
        "@media (max-width: 1707px) and (min-width: 1601px)": {
          maxWidth: "1350px",
          height: "500px",
        },
        "@media (max-width: 1824px) and (min-width: 1708px)": {
          maxWidth: "1425px",
          height: "500px",
        },
        "@media (max-width: 1920px) and (min-width: 1825px)": {
          maxWidth: "1530px",
          height: "500px",
        },
        "@media (max-width: 1921px)": {
          maxWidth: "1530px",
          height: "622px",
        },
      }}
    >
      <Table.Root
        interactive
        striped
        size="lg"
        borderRadius="8px"
        border={noBorder ? "" : `1px solid ${AppColors.WHITE_BORDER_1}`}
        stickyHeader
      >
        <Table.Header>
          <Table.Row>
            {!noSelection && !multipleSelection && (
              <Text
                padding="16px"
                color={AppColors.GRAY_TEXT}
                fontWeight={700}
                textTransform="uppercase"
                textAlign="center"
                css={{
                  "@media (max-width: 1350px) and (min-width: 1270px)": {
                    fontSize: "10px",
                  },
                  "@media (max-width: 1423px) and (min-width: 1351px)": {
                    fontSize: "10px",
                  },
                  "@media (max-width: 1600px) and (min-width: 1424px)": {
                    fontSize: "12px",
                  },
                  "@media (max-width: 1707px) and (min-width: 1601px)": {
                    fontSize: "12px",
                  },
                  "@media (max-width: 1824px) and (min-width: 1708px)": {
                    fontSize: "12px",
                  },
                  "@media (max-width: 1920px) and (min-width: 1825px)": {
                    fontSize: "12px",
                  },
                  "@media (max-width: 1921px)": {
                    fontSize: "12px",
                  },
                }}
              >
                SELECCIONAR
              </Text>
            )}
            {!noSelection && multipleSelection && (
              <Table.ColumnHeader w="6">
                <Checkbox.Root
                  top="1"
                  paddingLeft={3}
                  aria-label="Select all rows"
                  disabled={!multipleSelection}
                  checked={
                    indeterminate ? "indeterminate" : selection?.length > 0
                  }
                  onCheckedChange={handleHeaderCheckedChange}
                >
                  <Checkbox.HiddenInput data-testid="all-selection-checkbox" />
                  <Checkbox.Control
                    borderColor={AppColors.GRAY_BORDER_1}
                    cursor="pointer"
                  />
                </Checkbox.Root>
              </Table.ColumnHeader>
            )}

            {headers.map((header, index) => (
              <Table.ColumnHeader key={index + header.label}>
                <Text
                  padding="16px"
                  color={AppColors.GRAY_TEXT}
                  fontWeight={700}
                  textTransform="uppercase"
                  textAlign={header?.align ?? "inherit"}
                  css={{
                    "@media (max-width: 1350px) and (min-width: 1270px)": {
                      fontSize: "10px",
                    },
                    "@media (max-width: 1423px) and (min-width: 1351px)": {
                      fontSize: "10px",
                    },
                    "@media (max-width: 1600px) and (min-width: 1424px)": {
                      fontSize: "12px",
                    },
                    "@media (max-width: 1707px) and (min-width: 1601px)": {
                      fontSize: "12px",
                    },
                    "@media (max-width: 1824px) and (min-width: 1708px)": {
                      fontSize: "12px",
                    },
                    "@media (max-width: 1920px) and (min-width: 1825px)": {
                      fontSize: "12px",
                    },
                    "@media (max-width: 1921px)": {
                      fontSize: "12px",
                    },
                  }}
                >
                  {header?.label}
                </Text>
              </Table.ColumnHeader>
            ))}
            {!noActions && actionElements ? (
              <Table.ColumnHeader>
                <Flex padding="16px" justifyContent={headerLabelAlign ?? "end"}>
                  <Text
                    color={AppColors.GRAY_TEXT}
                    fontWeight={700}
                    textTransform="uppercase"
                    css={{
                      "@media (max-width: 1350px) and (min-width: 1270px)": {
                        fontSize: "10px",
                      },
                      "@media (max-width: 1423px) and (min-width: 1351px)": {
                        fontSize: "10px",
                      },
                      "@media (max-width: 1600px) and (min-width: 1424px)": {
                        fontSize: "12px",
                      },
                      "@media (max-width: 1707px) and (min-width: 1601px)": {
                        fontSize: "12px",
                      },
                      "@media (max-width: 1824px) and (min-width: 1708px)": {
                        fontSize: "12px",
                      },
                      "@media (max-width: 1920px) and (min-width: 1825px)": {
                        fontSize: "12px",
                      },
                      "@media (max-width: 1921px)": {
                        fontSize: "12px",
                      },
                    }}
                  >
                    {actionsHeaderLabel}
                  </Text>
                </Flex>
              </Table.ColumnHeader>
            ) : null}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {items?.map((item) => {
            return (
              <Table.Row
                key={item[rowId]}
                data-selected={
                  selection.includes(item?.[rowId]) ? "" : undefined
                }
              >
                {noSelection ? null : (
                  <Table.Cell
                    paddingX={multipleSelection ? "0" : "35px"}
                    width={multipleSelection ? "auto" : "100px"}
                  >
                    <Checkbox.Root
                      top="1"
                      paddingLeft={3}
                      aria-label="Select row"
                      checked={selection.includes(item?.[rowId])}
                      onCheckedChange={(changes) =>
                        handleItemCheckChange(changes, item)
                      }
                    >
                      <Checkbox.HiddenInput data-testid="row-selection-checkbox" />
                      <Checkbox.Control
                        borderColor={AppColors.GRAY_BORDER_1}
                        cursor="pointer"
                      />
                    </Checkbox.Root>
                  </Table.Cell>
                )}

                {headers.map((header) => (
                  <Table.Cell key={`${item[rowId]}-${header.value}`}>
                    <Text
                      as="div"
                      padding="16px"
                      fontWeight={400}
                      className="account-title-label"
                      color={AppColors.BLACK_TEXT_SECONDARY}
                      whiteSpace="wrap"
                      css={{
                        "@media (max-width: 1350px) and (min-width: 1270px)": {
                          fontSize: "10px",
                        },
                        "@media (max-width: 1423px) and (min-width: 1351px)": {
                          fontSize: "10px",
                        },
                        "@media (max-width: 1600px) and (min-width: 1424px)": {
                          fontSize: "12px",
                        },
                        "@media (max-width: 1707px) and (min-width: 1601px)": {
                          fontSize: "12px",
                        },
                        "@media (max-width: 1824px) and (min-width: 1708px)": {
                          fontSize: "12px",
                        },
                        "@media (max-width: 1920px) and (min-width: 1825px)": {
                          fontSize: "12px",
                        },
                        "@media (max-width: 1921px)": {
                          fontSize: "12px",
                        },
                      }}
                    >
                      {item[header.value] ?? "-"}
                    </Text>
                  </Table.Cell>
                ))}
                {!noActions && actionElements ? (
                  <Table.Cell>
                    <Flex
                      justifyContent={actionsAlign ?? "flex-end"}
                      paddingX="16px"
                    >
                      {
                        actionElements &&
                          cloneElement(actionElements(item), {
                            item,
                          } as Partial<unknown> & Attributes) // Pasamos `item` como prop
                      }
                    </Flex>
                  </Table.Cell>
                ) : null}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  );
};
